import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Loader2, Plus, Settings2, Trash2, ShoppingBag, Users, UserCheck, Zap, CalendarClock, UtensilsCrossed, GraduationCap, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TechWatermark } from "../components/TechWatermark";
import { mk } from "../lib/marketApi";
import { crm } from "../lib/crmApi";
import { inputCls, Labeled } from "../components/market/admin/ui";
import { toast } from "../hooks/use-toast";

const TEMPLATES = [
  { id: "mercado", name: "Mercado", desc: "Tienda en línea con carrito y cobro con Stripe", icon: ShoppingBag, ready: true },
  { id: "crm", name: "CRM inteligente", desc: "Prospectos, cotizaciones PDF, ventas, comisiones y WhatsApp", icon: Users, ready: true },
  { id: "personal", name: "Registro de personal", desc: "Empleados, asistencia y credenciales", icon: UserCheck },
  { id: "ventas", name: "Ventas directas", desc: "Página de venta enfocada en cerrar", icon: Zap },
  { id: "reservas", name: "Reservas y citas", desc: "Agenda en línea con anticipo", icon: CalendarClock },
  { id: "menu", name: "Menú con pedidos", desc: "Restaurantes y cafeterías", icon: UtensilsCrossed },
  { id: "cursos", name: "Membresías y cursos", desc: "Contenido para miembros", icon: GraduationCap },
];

const toSlug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);

const StoreFormDialog = ({ open, source, template = "mercado", onClose, onDone }) => {
  const isCrm = template === "crm";
  const base = isCrm ? "/c/" : "/m/";
  const [f, setF] = useState({ name: "", slug: "", owner_email: "", owner_password: "", domain: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    setF({ name: source ? `${source.name} (copia)` : "", slug: source ? `${source.slug}-copia` : "", owner_email: "", owner_password: "", domain: "" });
    setErr("");
  }, [open, source]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (isCrm) onDone(await crm(source ? `/admin/orgs/${source.id}/duplicate` : "/admin/orgs", { method: "POST", body: f }));
      else onDone(await mk(source ? `/admin/stores/${source.id}/duplicate` : "/admin/stores", { method: "POST", body: f }));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="store-form-dialog">
        <DialogHeader>
          <DialogTitle>{source ? `Duplicar "${source.name}"` : isCrm ? "Nuevo CRM inteligente" : "Nueva plataforma Mercado"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Labeled label={isCrm ? "Nombre de la empresa" : "Nombre de la tienda"}>
            <input required className={inputCls} value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value, slug: source ? x.slug : toSlug(e.target.value) }))} data-testid="store-form-name" />
          </Labeled>
          <Labeled label="Enlace" hint={`${window.location.origin}${base}${f.slug || "mi-empresa"}`}>
            <input required className={inputCls} value={f.slug} onChange={(e) => setF((x) => ({ ...x, slug: toSlug(e.target.value) }))} data-testid="store-form-slug" />
          </Labeled>
          <Labeled label="Dominio propio (opcional)"><input className={inputCls} value={f.domain} onChange={(e) => setF((x) => ({ ...x, domain: e.target.value }))} placeholder="mitienda.com" data-testid="store-form-domain" /></Labeled>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold text-slate-600">{isCrm ? "Acceso del administrador del CRM" : "Acceso del dueño de la tienda"}</p>
            <div className="space-y-2">
              <input required type="email" className={inputCls} placeholder="Correo del dueño" value={f.owner_email} onChange={(e) => setF((x) => ({ ...x, owner_email: e.target.value }))} data-testid="store-form-owner-email" />
              <input required minLength={6} className={inputCls} placeholder="Contraseña (mín. 6)" value={f.owner_password} onChange={(e) => setF((x) => ({ ...x, owner_password: e.target.value }))} data-testid="store-form-owner-password" />
            </div>
          </div>
          {err && <p className="text-sm text-red-600" data-testid="store-form-error">{err}</p>}
          <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white" data-testid="store-form-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {source ? "Duplicar" : isCrm ? "Crear CRM" : "Crear tienda"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StoreRow = ({ s, onManage, onDuplicate, onDelete }) => {
  const isCrm = s.template === "crm";
  const path = `${isCrm ? "/c/" : "/m/"}${s.slug}`;
  const url = s.domain ? `https://${s.domain}` : `${window.location.origin}${path}`;
  const Icon = isCrm ? Users : ShoppingBag;
  const copy = () => {
    navigator.clipboard?.writeText(url)?.catch(() => {});
    toast({ title: "Enlace copiado", description: url });
  };
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`platform-row-${s.slug}`}>
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: s.theme?.primary || "#2563EB" }}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900">{s.name}</p>
          <p className="truncate text-xs text-slate-500">{url.replace(/^https?:\/\//, "")}</p>
          <p className="mt-1 text-xs text-slate-500">
            {isCrm ? (
              <>CRM · {s.contacts} clientes · {s.users} usuarios · {s.whatsapp_ready ? <span className="text-emerald-600">WhatsApp conectado</span> : <span className="text-amber-600">Sin WhatsApp</span>}</>
            ) : (
              <>{s.products} productos · {s.orders} ventas · {s.payments_ready ? <span className="text-emerald-600">Stripe conectado</span> : <span className="text-amber-600">Sin Stripe</span>}</>
            )}
            {s.status === "paused" && <span className="ml-1 text-red-500">· En pausa</span>}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={onManage} className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white" data-testid={`platform-manage-${s.slug}`}><Settings2 className="h-3.5 w-3.5" /> Administrar</button>
        <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-2 text-xs font-medium text-slate-700" data-testid={`platform-copy-${s.slug}`}><Link2 className="h-3.5 w-3.5" /> Copiar enlace</button>
        <a href={path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-2 text-xs font-medium text-slate-700" data-testid={`platform-open-${s.slug}`}><ExternalLink className="h-3.5 w-3.5" /> Abrir</a>
        <button onClick={onDuplicate} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-2 text-xs font-medium text-slate-700" data-testid={`platform-duplicate-${s.slug}`}><Copy className="h-3.5 w-3.5" /> Duplicar</button>
        <button onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50" data-testid={`platform-delete-${s.slug}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
};

export default function Platforms() {
  const navigate = useNavigate();
  const [stores, setStores] = useState(null);
  const [form, setForm] = useState(null);
  const load = () =>
    Promise.all([mk("/admin/stores").catch(() => []), crm("/admin/orgs").catch(() => [])]).then(([a, b]) => setStores([...b, ...a]));
  useEffect(() => {
    localStorage.setItem("uai_creator", "1");
  }, []);
  useEffect(() => {
    load();
  }, []);

  const manage = async (s) => {
    if (s.template === "crm") {
      const d = await crm(`/admin/orgs/${s.id}/access`, { method: "POST" });
      localStorage.setItem(`crm_auth_${s.slug}`, JSON.stringify(d));
      return navigate(`/c/${s.slug}`);
    }
    const d = await mk(`/admin/stores/${s.id}/access`, { method: "POST" });
    localStorage.setItem(`mk_owner_${s.slug}`, JSON.stringify({ token: d.token, sid: s.id }));
    navigate(`/m/${s.slug}/admin`);
  };
  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.name}" con toda su información? Esta acción no se puede deshacer.`)) return;
    if (s.template === "crm") await crm(`/admin/orgs/${s.id}`, { method: "DELETE" });
    else await mk(`/admin/stores/${s.id}`, { method: "DELETE" });
    load();
  };

  return (
    <div className="relative min-h-screen bg-[#0b1530]">
      <TechWatermark />
      <header className="safe-top sticky top-0 z-30 border-b border-white/10 bg-white/95 shadow-lg shadow-black/20 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 pb-4 pt-6">
          <button onClick={() => navigate("/")} className="rounded-full p-2 hover:bg-slate-100" data-testid="platforms-back"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="flex-1 text-lg font-bold text-slate-900">Plataformas</h1>
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-5xl space-y-8 px-4 py-8">
        <section>
          <h2 className="text-base font-semibold text-white md:text-lg">Crear plataforma</h2>
          <p className="text-sm text-blue-100/70">Elige una estructura lista y personalízala por completo.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                disabled={!t.ready}
                onClick={() => setForm({ source: null, template: t.id })}
                className={`group relative flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-transform ${t.ready ? "border-white/20 bg-white hover:-translate-y-0.5" : "cursor-not-allowed border-white/10 bg-white/10"}`}
                data-testid={`template-${t.id}`}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${t.ready ? "bg-blue-600 text-white" : "bg-white/10 text-blue-100/60"}`}><t.icon className="h-5 w-5" /></span>
                <span className={`text-sm font-semibold ${t.ready ? "text-slate-900" : "text-white/80"}`}>{t.name}</span>
                <span className={`text-xs ${t.ready ? "text-slate-500" : "text-blue-100/50"}`}>{t.desc}</span>
                {t.ready ? <Plus className="absolute right-3 top-3 h-4 w-4 text-blue-600" /> : <span className="absolute right-2 top-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-blue-100/70">Próximamente</span>}
              </button>
            ))}
          </div>
        </section>
        <section>
          <h2 className="text-base font-semibold text-white md:text-lg">Mis plataformas</h2>
          {!stores && <Loader2 className="mt-6 h-6 w-6 animate-spin text-blue-100/60" />}
          {stores && !stores.length && <p className="mt-4 rounded-2xl border border-dashed border-blue-300/40 bg-white/10 p-8 text-center text-sm text-blue-100/80" data-testid="platforms-empty">Aún no tienes plataformas. Crea tu primer Mercado arriba.</p>}
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {(stores || []).map((s) => (
              <StoreRow key={`${s.template}-${s.id}`} s={s} onManage={() => manage(s)} onDuplicate={() => setForm({ source: s, template: s.template })} onDelete={() => remove(s)} />
            ))}
          </div>
        </section>
      </main>
      <StoreFormDialog
        open={!!form}
        source={form?.source}
        template={form?.template}
        onClose={() => setForm(null)}
        onDone={() => {
          setForm(null);
          load();
        }}
      />
    </div>
  );
}
